/**
 * Error handling utilities for the web application
 */

/**
 * Error types for zone-related operations
 */
export enum ZoneErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  ZONE_NOT_FOUND = 'ZONE_NOT_FOUND',
  ZONE_LOAD_FAILED = 'ZONE_LOAD_FAILED',
  ZONE_SELECTION_FAILED = 'ZONE_SELECTION_FAILED',
  DELIVERY_COST_CALCULATION_FAILED = 'DELIVERY_COST_CALCULATION_FAILED',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * User-friendly error messages for zone operations
 */
export const ZoneErrorMessages = {
  [ZoneErrorType.NETWORK_ERROR]: 'Unable to connect to the server. Please check your internet connection and try again.',
  [ZoneErrorType.ZONE_NOT_FOUND]: 'The selected delivery zone is no longer available. Please select a different zone.',
  [ZoneErrorType.ZONE_LOAD_FAILED]: 'Failed to load delivery zones. Please try again.',
  [ZoneErrorType.ZONE_SELECTION_FAILED]: 'Failed to select delivery zone. Please try again.',
  [ZoneErrorType.DELIVERY_COST_CALCULATION_FAILED]: 'Failed to calculate delivery cost. Please try again.',
  [ZoneErrorType.VALIDATION_ERROR]: 'Invalid zone data. Please refresh the page and try again.',
  [ZoneErrorType.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again later.'
};

/**
 * Custom error class for zone-related errors
 */
export class ZoneError extends Error {
  constructor(
    public type: ZoneErrorType,
    message?: string,
    public originalError?: Error
  ) {
    super(message || ZoneErrorMessages[type]);
    this.name = 'ZoneError';
  }
}

/**
 * Determine error type from error object
 */
export const getZoneErrorType = (error: any): ZoneErrorType => {
  // Network errors
  if (
    error?.message?.includes('fetch') ||
    error?.message?.includes('network') ||
    error?.message?.includes('ECONNREFUSED') ||
    error?.message?.includes('ETIMEDOUT') ||
    error?.code === 'NETWORK_ERROR'
  ) {
    return ZoneErrorType.NETWORK_ERROR;
  }

  // Not found errors
  if (
    error?.status === 404 ||
    error?.message?.includes('not found') ||
    error?.code === 'NOT_FOUND'
  ) {
    return ZoneErrorType.ZONE_NOT_FOUND;
  }

  // Validation errors
  if (
    error?.status === 400 ||
    error?.message?.includes('validation') ||
    error?.code === 'VALIDATION_ERROR'
  ) {
    return ZoneErrorType.VALIDATION_ERROR;
  }

  // Default to unknown error
  return ZoneErrorType.UNKNOWN_ERROR;
};

/**
 * Get user-friendly error message from error object
 */
export const getZoneErrorMessage = (error: any): string => {
  if (error instanceof ZoneError) {
    return error.message;
  }

  const errorType = getZoneErrorType(error);
  return ZoneErrorMessages[errorType];
};

/**
 * Log error for debugging (can be extended to send to error tracking service)
 */
export const logZoneError = (
  error: Error | ZoneError,
  context: {
    operation: string;
    zoneId?: string;
    additionalInfo?: Record<string, any>;
  }
): void => {
  const timestamp = new Date().toISOString();
  const errorLog = {
    timestamp,
    operation: context.operation,
    error: {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...(error instanceof ZoneError && {
        type: error.type,
        originalError: error.originalError?.message
      })
    },
    context: {
      zoneId: context.zoneId,
      ...context.additionalInfo
    }
  };

  console.error('[Zone Error]', errorLog);

  // In production, you might want to send this to an error tracking service
  // Example: Sentry.captureException(error, { contexts: { zone: errorLog } });
};

/**
 * Retry helper with exponential backoff
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    onRetry?: (attempt: number, error: Error) => void;
  } = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    onRetry
  } = options;

  let lastError: Error;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries - 1) {
        const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);
        
        if (onRetry) {
          onRetry(attempt + 1, lastError);
        }

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError!;
};

/**
 * Check if error is retryable
 */
export const isRetryableError = (error: any): boolean => {
  const errorType = getZoneErrorType(error);
  
  // Network errors and server errors are retryable
  return (
    errorType === ZoneErrorType.NETWORK_ERROR ||
    error?.status >= 500 ||
    error?.message?.includes('timeout')
  );
};

/**
 * Format error for display in UI
 */
export const formatErrorForDisplay = (error: any): {
  title: string;
  message: string;
  canRetry: boolean;
} => {
  const errorType = getZoneErrorType(error);
  const message = getZoneErrorMessage(error);
  const canRetry = isRetryableError(error);

  let title = 'Error';
  
  switch (errorType) {
    case ZoneErrorType.NETWORK_ERROR:
      title = 'Connection Error';
      break;
    case ZoneErrorType.ZONE_NOT_FOUND:
      title = 'Zone Not Found';
      break;
    case ZoneErrorType.ZONE_LOAD_FAILED:
      title = 'Loading Failed';
      break;
    case ZoneErrorType.VALIDATION_ERROR:
      title = 'Invalid Data';
      break;
    default:
      title = 'Unexpected Error';
  }

  return { title, message, canRetry };
};
